import PlusIcon from '@assets/icons/plus.svg';
import React, { useState } from 'react';
import { CreateOrderGuideDropdownContent } from '@/experiences/sales-portal/components/create-order-guide/create-order-guide-dropdown';
import { Platform } from 'react-native';
import { QuickActionDropdownItem } from '@/experiences/sales-portal/components/quick-actions-card';
import { useChatSalesPortalDynamicConfigValue } from '@/experiences/sales-portal/blocs/hooks/use-chat-dynamic-config';

interface CreateOrderGuideQuickActionParams {
  chatUUID: string | null;
  label: string;
  onOpenChange?: (open: boolean) => void;
  onSelectOnlineMenu?: () => void;
  onSelectUploadPhoto?: () => void;
  supplierUUID: string;
}

interface CreateOrderGuideQuickActionResult {
  closeOnlineMenuModal: () => void;
  closeUploadPhotoModal: () => void;
  isOnlineMenuModalOpen: boolean;
  isUploadPhotoModalOpen: boolean;
  loading: boolean;
  quickAction: QuickActionDropdownItem | null;
}

/**
 * Builds the "Generate order guide" quick action shared by account detail and
 * prospect detail, and owns the open state of its online-menu and upload-photo modals.
 *
 * The online-menu option is gated by `menu_og_generation_enabled` and the
 * upload-photo option (web only) by `menu_image_og_generation_enabled`. The chat
 * config takes precedence while the supplier config is the fallback
 */
export const useCreateOrderGuideQuickAction = ({
  chatUUID,
  label,
  onOpenChange,
  onSelectOnlineMenu,
  onSelectUploadPhoto,
  supplierUUID,
}: CreateOrderGuideQuickActionParams): CreateOrderGuideQuickActionResult => {
  const [isOnlineMenuModalOpen, setIsOnlineMenuModalOpen] = useState(false);
  const [isUploadPhotoModalOpen, setIsUploadPhotoModalOpen] = useState(false);

  const {
    loading: onlineMenuConfigLoading,
    value: isOnlineMenuOrderGuideEnabled,
  } = useChatSalesPortalDynamicConfigValue(
    supplierUUID,
    'menu_og_generation_enabled',
    chatUUID,
  );
  const {
    loading: uploadPhotoConfigLoading,
    value: isUploadPhotoOrderGuideEnabled,
  } = useChatSalesPortalDynamicConfigValue(
    supplierUUID,
    'menu_image_og_generation_enabled',
    chatUUID,
  );

  const showOnlineMenuOption = Boolean(isOnlineMenuOrderGuideEnabled);
  const showUploadPhotoOption =
    Platform.OS === 'web' && Boolean(isUploadPhotoOrderGuideEnabled);

  const handleSelectOnlineMenu = () => {
    onSelectOnlineMenu?.();
    setIsOnlineMenuModalOpen(true);
  };
  const handleSelectUploadPhoto = () => {
    onSelectUploadPhoto?.();
    setIsUploadPhotoModalOpen(true);
  };

  const quickAction: QuickActionDropdownItem | null =
    showOnlineMenuOption || showUploadPhotoOption
      ? {
          dropdownContent: (close: () => void) => (
            <CreateOrderGuideDropdownContent
              close={close}
              onSelectOnlineMenu={handleSelectOnlineMenu}
              onSelectUploadPhotoOrFile={handleSelectUploadPhoto}
              showOnlineMenuOption={showOnlineMenuOption}
              showUploadPhotoOrFileOption={showUploadPhotoOption}
            />
          ),
          Icon: PlusIcon,
          label,
          onOpenChange,
        }
      : null;

  return {
    closeOnlineMenuModal: () => setIsOnlineMenuModalOpen(false),
    closeUploadPhotoModal: () => setIsUploadPhotoModalOpen(false),
    isOnlineMenuModalOpen,
    isUploadPhotoModalOpen,
    loading: onlineMenuConfigLoading || uploadPhotoConfigLoading,
    quickAction,
  };
};
